import type { MotionLinkCleaningStats } from './motionlinkDataCleaner.js';
import type {
  MotionLinkVisualizationData,
  MotionLinkVisualizationQuality,
  MotionLinkVisualizationSample,
} from './motionlinkJsonLoader.js';

export type MotionLinkPathMode = 'raw' | 'cleaned' | 'both';
export type MotionLinkAnimateSource = 'raw' | 'cleaned';

export interface MotionLinkComparisonMetrics {
  sampleCount: number;
  durationMs: number;
  estimatedDistance: number;
  jitterScore: number;
  shakeScore: number;
  driftRisk: 'low' | 'medium' | 'high' | 'unknown';
  smoothingAmount: number;
  confidence: number | null;
  droppedSamples: number;
  timestampGaps: number;
}

export interface MotionLinkMovementLabel {
  type:
    | 'pan_left'
    | 'pan_right'
    | 'tilt_up'
    | 'tilt_down'
    | 'push_in'
    | 'pull_out'
    | 'handheld_shake'
    | 'return_reset'
    | 'static_locked_off';
  confidence: number;
  reason: string;
}

export interface MotionLinkOrientationGraphPoint {
  timestampMs: number;
  yaw: number;
  pitch: number;
  roll: number;
}

export interface MotionLinkOrientationGraphData {
  points: MotionLinkOrientationGraphPoint[];
  hasOrientation: boolean;
  warnings: string[];
}

export interface MotionLinkRawVsCleanedAnalysis {
  raw: MotionLinkComparisonMetrics;
  cleaned: MotionLinkComparisonMetrics;
  jitterReduction: number;
  distanceReduction: number;
  movementLabels: MotionLinkMovementLabel[];
  orientationGraph: MotionLinkOrientationGraphData;
  copy: string;
}

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));
const clamp01 = (value: number): number => clamp(value, 0, 1);

function distance(a: MotionLinkVisualizationSample['position'], b: MotionLinkVisualizationSample['position']): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

function pathDistance(samples: readonly MotionLinkVisualizationSample[]): number {
  let total = 0;
  for (let index = 1; index < samples.length; index++) {
    total += distance(samples[index - 1]!.position, samples[index]!.position);
  }
  return total;
}

function netDistance(samples: readonly MotionLinkVisualizationSample[]): number {
  if (samples.length < 2) return 0;
  return distance(samples[0]!.position, samples[samples.length - 1]!.position);
}

function averageConfidence(samples: readonly MotionLinkVisualizationSample[]): number | null {
  const values = samples
    .map((sample) => sample.confidence)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

export function calculateMotionLinkJitterScore(samples: readonly MotionLinkVisualizationSample[]): number {
  if (samples.length < 3) return 0;
  let total = 0;
  let count = 0;
  for (let index = 1; index < samples.length - 1; index++) {
    const previous = samples[index - 1]!;
    const current = samples[index]!;
    const next = samples[index + 1]!;
    const expected = {
      x: (previous.position.x + next.position.x) / 2,
      y: (previous.position.y + next.position.y) / 2,
      z: (previous.position.z + next.position.z) / 2,
    };
    const localScale = Math.max(distance(previous.position, next.position), 0.015);
    total += clamp01(distance(current.position, expected) / localScale);
    count += 1;
  }
  return count ? total / count : 0;
}

function calculateShakeScore(samples: readonly MotionLinkVisualizationSample[]): number {
  if (samples.length < 4) return 0;
  let directionChanges = 0;
  let count = 0;
  for (let index = 2; index < samples.length; index++) {
    const a = samples[index - 2]!;
    const b = samples[index - 1]!;
    const c = samples[index]!;
    const v1 = { x: b.position.x - a.position.x, z: b.position.z - a.position.z };
    const v2 = { x: c.position.x - b.position.x, z: c.position.z - b.position.z };
    const m1 = Math.hypot(v1.x, v1.z);
    const m2 = Math.hypot(v2.x, v2.z);
    if (m1 < 0.003 || m2 < 0.003) continue;
    const dot = clamp((v1.x * v2.x + v1.z * v2.z) / (m1 * m2), -1, 1);
    directionChanges += (1 - dot) / 2;
    count += 1;
  }
  const directionScore = count ? directionChanges / count : 0;
  return clamp01(directionScore * 0.65 + calculateMotionLinkJitterScore(samples) * 0.35);
}

function driftRiskFor(samples: readonly MotionLinkVisualizationSample[], jitterScore: number): MotionLinkComparisonMetrics['driftRisk'] {
  if (!samples.length) return 'unknown';
  const distanceTotal = pathDistance(samples);
  if (distanceTotal < 0.04) return jitterScore > 0.55 ? 'medium' : 'low';
  const returnRatio = netDistance(samples) / Math.max(distanceTotal, 1e-6);
  if (returnRatio < 0.12 && distanceTotal > 0.25) return 'high';
  if (returnRatio < 0.25 || jitterScore > 0.5) return 'medium';
  return 'low';
}

function metricsFor(
  data: MotionLinkVisualizationData,
  quality?: MotionLinkVisualizationQuality,
  overrides: Partial<Pick<MotionLinkComparisonMetrics, 'smoothingAmount' | 'droppedSamples' | 'timestampGaps'>> = {},
): MotionLinkComparisonMetrics {
  const jitterScore = calculateMotionLinkJitterScore(data.samples);
  return {
    sampleCount: data.samples.length,
    durationMs: data.durationMs,
    estimatedDistance: pathDistance(data.samples),
    jitterScore,
    shakeScore: calculateShakeScore(data.samples),
    driftRisk: driftRiskFor(data.samples, jitterScore),
    smoothingAmount: overrides.smoothingAmount ?? 0,
    confidence: averageConfidence(data.samples) ?? quality?.averageConfidence ?? null,
    droppedSamples: overrides.droppedSamples ?? quality?.droppedSampleCount ?? 0,
    timestampGaps: overrides.timestampGaps ?? quality?.timestampGapCount ?? 0,
  };
}

function normalizeAngle(value: number): number {
  let angle = value;
  while (angle > Math.PI) angle -= Math.PI * 2;
  while (angle < -Math.PI) angle += Math.PI * 2;
  return angle;
}

function firstLastRotation(samples: readonly MotionLinkVisualizationSample[]): { yaw: number; pitch: number; roll: number } | null {
  const first = samples.find((sample) => sample.rotation);
  const last = [...samples].reverse().find((sample) => sample.rotation);
  if (!first?.rotation || !last?.rotation) return null;
  return {
    yaw: normalizeAngle((last.rotation.yaw ?? 0) - (first.rotation.yaw ?? 0)),
    pitch: normalizeAngle((last.rotation.pitch ?? 0) - (first.rotation.pitch ?? 0)),
    roll: normalizeAngle((last.rotation.roll ?? 0) - (first.rotation.roll ?? 0)),
  };
}

export function detectMotionLinkMovementLabels(data: MotionLinkVisualizationData): MotionLinkMovementLabel[] {
  const samples = data.samples;
  if (!samples.length) return [];
  const labels: MotionLinkMovementLabel[] = [];
  const totalDistance = pathDistance(samples);
  const totalNetDistance = netDistance(samples);
  const rotationDelta = firstLastRotation(samples);
  const start = samples[0]!;
  const end = samples[samples.length - 1]!;
  const dx = end.position.x - start.position.x;
  const dz = end.position.z - start.position.z;
  const shakeScore = calculateShakeScore(samples);
  const yawThreshold = 5 * Math.PI / 180;
  const pitchThreshold = 5 * Math.PI / 180;
  const translationThreshold = Math.max(0.08, totalDistance * 0.18);

  if (totalDistance < 0.04 && (!rotationDelta || (Math.abs(rotationDelta.yaw) < yawThreshold && Math.abs(rotationDelta.pitch) < pitchThreshold))) {
    labels.push({ type: 'static_locked_off', confidence: 0.82, reason: 'Very small path distance and low rotation change.' });
  }

  if (rotationDelta && Math.abs(rotationDelta.yaw) >= yawThreshold) {
    labels.push({
      type: rotationDelta.yaw > 0 ? 'pan_right' : 'pan_left',
      confidence: clamp01(Math.abs(rotationDelta.yaw) / (24 * Math.PI / 180)),
      reason: `Yaw changed by ${(rotationDelta.yaw * 180 / Math.PI).toFixed(1)}°.`,
    });
  }

  if (rotationDelta && Math.abs(rotationDelta.pitch) >= pitchThreshold) {
    labels.push({
      type: rotationDelta.pitch > 0 ? 'tilt_up' : 'tilt_down',
      confidence: clamp01(Math.abs(rotationDelta.pitch) / (20 * Math.PI / 180)),
      reason: `Pitch changed by ${(rotationDelta.pitch * 180 / Math.PI).toFixed(1)}°.`,
    });
  }

  if (Math.abs(dz) >= translationThreshold) {
    labels.push({
      type: dz < 0 ? 'push_in' : 'pull_out',
      confidence: clamp01(Math.abs(dz) / Math.max(totalDistance, 0.1)),
      reason: `Net Z motion was ${dz.toFixed(3)} m in the local ARKit path.`,
    });
  }

  if (Math.abs(dx) >= translationThreshold && !rotationDelta) {
    labels.push({
      type: dx > 0 ? 'pan_right' : 'pan_left',
      confidence: clamp01(Math.abs(dx) / Math.max(totalDistance, 0.1)),
      reason: `Net X motion was ${dx.toFixed(3)} m without usable yaw data.`,
    });
  }

  if (shakeScore > 0.35) {
    labels.push({
      type: 'handheld_shake',
      confidence: clamp01(shakeScore),
      reason: `Short-term direction changes produced a shake score of ${Math.round(shakeScore * 100)}%.`,
    });
  }

  if (totalDistance > 0.18 && totalNetDistance / Math.max(totalDistance, 1e-6) < 0.25) {
    labels.push({
      type: 'return_reset',
      confidence: clamp01(1 - totalNetDistance / Math.max(totalDistance, 1e-6)),
      reason: 'The path travels but ends close to the starting position.',
    });
  }

  return labels.sort((a, b) => b.confidence - a.confidence).slice(0, 5);
}

export function extractMotionLinkOrientationGraphData(
  data: MotionLinkVisualizationData,
  maxPoints = 240,
): MotionLinkOrientationGraphData {
  const oriented = data.samples.filter((sample) =>
    Number.isFinite(sample.rotation?.yaw) ||
    Number.isFinite(sample.rotation?.pitch) ||
    Number.isFinite(sample.rotation?.roll),
  );
  if (!oriented.length) {
    return {
      points: [],
      hasOrientation: false,
      warnings: ['No yaw/pitch/roll orientation samples were available.'],
    };
  }
  const step = Math.max(1, Math.ceil(oriented.length / Math.max(1, maxPoints)));
  const points = oriented
    .filter((_, index) => index % step === 0 || index === oriented.length - 1)
    .map((sample) => ({
      timestampMs: sample.timestampMs,
      yaw: sample.rotation?.yaw ?? 0,
      pitch: sample.rotation?.pitch ?? 0,
      roll: sample.rotation?.roll ?? 0,
    }));
  return {
    points,
    hasOrientation: true,
    warnings: [],
  };
}

export function normalizeMotionLinkVisualizerState(input: {
  pathMode?: string;
  animateSource?: string;
}): { pathMode: MotionLinkPathMode; animateSource: MotionLinkAnimateSource } {
  const pathMode: MotionLinkPathMode =
    input.pathMode === 'raw' || input.pathMode === 'both' || input.pathMode === 'cleaned'
      ? input.pathMode
      : 'cleaned';
  const animateSource: MotionLinkAnimateSource = input.animateSource === 'raw' ? 'raw' : 'cleaned';
  return {
    pathMode,
    animateSource: pathMode === 'raw' && animateSource === 'cleaned' ? 'raw' : animateSource,
  };
}

export function createMotionLinkRawVsCleanedAnalysis(
  rawData: MotionLinkVisualizationData,
  cleanedData: MotionLinkVisualizationData,
  quality?: MotionLinkVisualizationQuality,
  cleaningStats?: MotionLinkCleaningStats,
): MotionLinkRawVsCleanedAnalysis {
  const rawDistance = pathDistance(rawData.samples);
  const cleanedDistance = pathDistance(cleanedData.samples);
  const smoothingAmount = rawDistance > 0 ? clamp01((rawDistance - cleanedDistance) / rawDistance) : 0;
  const raw = metricsFor(rawData, quality, { smoothingAmount: 0 });
  const cleaned = metricsFor(cleanedData, quality, {
    smoothingAmount,
    droppedSamples: cleaningStats
      ? cleaningStats.impossibleJumpSamplesAdjusted + cleaningStats.outlierSamplesAdjusted + cleaningStats.lowConfidenceSamplesAdjusted
      : 0,
    timestampGaps: quality?.timestampGapCount ?? 0,
  });
  const jitterReduction = raw.jitterScore > 0 ? clamp01((raw.jitterScore - cleaned.jitterScore) / raw.jitterScore) : 0;
  const distanceReduction = rawDistance > 0 ? clamp01((rawDistance - cleanedDistance) / rawDistance) : 0;

  return {
    raw,
    cleaned,
    jitterReduction,
    distanceReduction,
    movementLabels: detectMotionLinkMovementLabels(cleanedData),
    orientationGraph: extractMotionLinkOrientationGraphData(cleanedData),
    copy: 'Raw vs Cleaned shows how MotionLink stabilizes sensor capture for visual inspection. This is not a calibrated 3D camera solve.',
  };
}
